// Información: who/what this conversation is, and every verb about it — favourite, mute and the
// push level (two separate properties, shared/API.md §8.3.2), edit, close / leave / archive.
// The verbs are gated by the reader's resolved permissions (§26.1/§27.1), never by role names.
import { useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import { ChannelFavourites } from '../../../core/channelFavourites';
import { ChannelInfoPresentation } from '../../../core/channelInfoPresentation';
import { ChannelMute } from '../../../core/channelMute';
import { ChannelNotificationPreferences, type PushLevel, type State as NotifyState } from '../../../core/channelNotificationPreferences';
import { ChannelSettingsForm } from '../../../core/channelSettingsForm';
import { DirectChannelVisibility } from '../../../core/directChannelVisibility';
import { userDisplayName } from '../../../core/channelListDerivation';
import type { ChannelRecord } from '../../../core/records';
import type { ChannelPanelSnapshot } from '../../../data/features/channelPanel';
import { dmOtherParticipantId, dmPartnerId } from '../../../data/sync/mapping';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { MessageBody, type BodyContext } from '../../conversation/MessageBody';
import { Button, ConfirmButton, Group, InlineStatus, ListRow } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import type { PanelTab } from './panelModel';
import { SettingsDialog } from './SettingsDialog';

const LEVEL_LABEL: Record<PushLevel, [string, string]> = {
  default: ['channelNotifications.default', 'channelNotifications.defaultHint'],
  all: ['channelNotifications.all', 'channelNotifications.allHint'],
  mention: ['channelNotifications.mention', 'channelNotifications.mentionHint'],
  none: ['channelNotifications.none', 'channelNotifications.noneHint'],
};

export function InfoTab({ snap, channel, name, onClose, onOpenTab }: { snap: ChannelPanelSnapshot; channel: ChannelRecord; name: string; onClose(): void; onOpenTab(tab: PanelTab): void }) {
  const shell = useShell();
  const [favouriteBusy, setFavouriteBusy] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const permissions = useMemo(() => new Set(snap.permissions), [snap.permissions]);
  const me = snap.currentUserId ?? '';
  const archived = channel.deleteAt !== 0;

  // The member row is the truth for both notification properties (refreshed on open); a change in
  // flight shows its destination at once — the worker writes the row first, but the switch must
  // not wait for a store round trip to move under the pointer.
  const stored: NotifyState = ChannelNotificationPreferences.derive(snap.member?.notifyPush, snap.member?.markUnread);
  const [pending, setPending] = useState<NotifyState | null>(null);
  const notify = pending ?? stored;

  const users = useMemo(() => new Map(snap.users.map((u) => [u.id, u])), [snap.users]);
  const partnerId = channel.type === 'D' ? dmPartnerId(channel.name, me) : null;
  const partner = partnerId ? users.get(partnerId) ?? null : null;

  const bodyCtx: BodyContext = useMemo(
    () => ({
      knownUsernames: new Set(snap.users.map((u) => u.username)),
      currentUsername: users.get(me)?.username ?? null,
      customEmoji: {},
      onMention: (username: string) => shell.openUser({ username }),
    }),
    [snap.users, users, me, shell],
  );

  const favouriteTeam = ChannelFavourites.sidebarTeamId(channel.teamId, shell.team?.id ?? null);
  const canFavourite = ChannelFavourites.canFavourite(channel.type, channel.deleteAt) && favouriteTeam !== null;
  const favouriteState = ChannelFavourites.state(snap.isFavourite, favouriteBusy);
  const toggleFavourite = () => {
    if (favouriteBusy) return;
    setFavouriteBusy(true);
    data
      .call('setChannelFavourite', channel.id, !snap.isFavourite, shell.team?.id ?? null)
      .then((ok) => !ok && shell.toast(t('channel.action.failed'), 'error'))
      .catch(() => shell.toast(t('channel.action.failed'), 'error'))
      .finally(() => setFavouriteBusy(false));
  };

  const saveNotify = (next: NotifyState, failure: string) => {
    if (notifyBusy) return;
    const props = ChannelNotificationPreferences.changedProps(stored, next);
    if (!Object.keys(props).length) return;
    setNotifyBusy(true);
    setPending(next);
    data
      .call('setChannelNotifyProps', channel.id, props)
      .then((ok) => !ok && shell.toast(failure, 'error'))
      .catch(() => shell.toast(failure, 'error'))
      .finally(() => {
        setPending(null);
        setNotifyBusy(false);
      });
  };

  const canEdit = !archived && ChannelSettingsForm.canEditProperties(channel.type, permissions);
  const canLeave = ChannelInfoPresentation.canLeave(channel.type) && !archived;
  const canArchive = !archived && ChannelInfoPresentation.canArchive(channel.type, channel.name, permissions);
  const closePreference = DirectChannelVisibility.preferenceFor(channel.type, channel.id, dmOtherParticipantId(channel.name, me));

  const act = (call: () => Promise<boolean>) => {
    setFailed(false);
    call().then(
      (ok) => (ok ? onClose() : setFailed(true)),
      () => setFailed(true),
    );
  };

  const header = channel.header?.trim() ?? '';
  const purpose = channel.purpose?.trim() ?? '';

  return (
    <>
      {partner && partnerId ? (
        <div className="cp-hero">
          <Avatar userId={partner.id} name={userDisplayName(partner)} size={56} version={partner.lastPictureUpdate} presence={snap.statuses[partner.id] ?? null} />
          <div className="cp-hero-text">
            <div className="cp-hero-name">{userDisplayName(partner)}</div>
            <div className="cp-hero-sub">@{partner.username}</div>
            {snap.partnerPosition ? <div className="cp-hero-sub">{snap.partnerPosition}</div> : null}
          </div>
          <Button icon="person" onClick={() => shell.openUser({ userId: partner.id })}>
            {t('web.channelPanel.openProfile')}
          </Button>
        </div>
      ) : (
        <div className="cp-hero">
          <span className="cp-glyph" aria-hidden>
            <Icon name={channel.type === 'P' ? 'lock' : channel.type === 'G' ? 'people' : channel.type === 'D' ? 'person' : 'hash'} size={22} />
          </span>
          <div className="cp-hero-text">
            <div className="cp-hero-name">{name}</div>
            {snap.memberCount !== null && channel.type !== 'D' ? (
              <button className="cp-hero-sub cp-link" onClick={() => onOpenTab('members')}>
                {tf('channel.info.members', snap.memberCount)}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {header ? (
        <Group title={t('channel.info.header')}>
          <div className="cp-text">
            <MessageBody message={header} ctx={bodyCtx} />
          </div>
        </Group>
      ) : null}
      {purpose && channel.type !== 'D' ? (
        <Group title={t('channel.info.purpose')}>
          <div className="cp-text">{purpose}</div>
        </Group>
      ) : null}

      <Group>
        {canFavourite ? (
          <ListRow
            leading={<Icon name="heart" size={18} className={snap.isFavourite ? 'cp-on' : undefined} />}
            title={snap.isFavourite ? t('channel.unfavourite') : t('channel.favourite')}
            trailing={favouriteState === 'working' ? <span>{t('web.channelPanel.working')}</span> : null}
            onClick={toggleFavourite}
          />
        ) : null}
        {!archived ? (
          <SwitchRow
            icon="bellSlash"
            title={t('channelNotifications.mute')}
            subtitle={snap.inMutedCategory && !notify.isMuted ? t('web.channelPanel.mutedByCategory') : t('channelNotifications.muteHint')}
            on={notify.isMuted}
            disabled={notifyBusy}
            onChange={(on) => saveNotify({ ...notify, isMuted: on }, t('channelNotifications.muteFailed'))}
          />
        ) : null}
        {canEdit ? <ListRow leading={<Icon name="pencilLine" size={18} />} title={channel.type === 'D' || channel.type === 'G' ? t('web.channelPanel.editHeader') : t('settings.title')} trailing={<Icon name="chevronRight" size={15} />} onClick={() => setEditing(true)} /> : null}
      </Group>

      {!archived ? (
        <Group title={t('channelNotifications.push')}>
          {ChannelNotificationPreferences.pushLevels.map((level) => (
            <ListRow
              key={level}
              title={t(LEVEL_LABEL[level][0])}
              subtitle={t(LEVEL_LABEL[level][1])}
              active={notify.pushLevel === level}
              trailing={notify.pushLevel === level ? <Icon name="check" size={16} className="cp-on" /> : null}
              onClick={() => saveNotify({ ...notify, pushLevel: level }, t('channel.action.failed'))}
            />
          ))}
        </Group>
      ) : null}

      {closePreference || canLeave || canArchive ? (
        <div className="cp-actions">
          {failed ? <InlineStatus kind="error" text={t('channel.action.failed')} /> : null}
          {closePreference ? (
            <Button icon="x" onClick={() => act(() => data.call('closeConversation', channel.id, shell.team?.id ?? null))}>
              {t('channel.action.close')}
            </Button>
          ) : null}
          {canLeave ? <ConfirmButton icon="signOut" label={t('channel.action.leave')} confirmLabel={t('channel.action.leaveConfirm')} onConfirm={() => act(() => data.call('leaveChannel', channel.id))} /> : null}
          {canArchive ? <ConfirmButton icon="archive" label={t('channel.action.archive')} confirmLabel={t('channel.action.archiveConfirm')} onConfirm={() => act(() => data.call('archiveChannel', channel.id))} /> : null}
        </div>
      ) : null}

      {editing ? <SettingsDialog channel={channel} name={name} permissions={permissions} onClose={() => setEditing(false)} /> : null}
    </>
  );
}

/** A list row whose trailing control is a switch; the whole row toggles it. */
function SwitchRow({ icon, title, subtitle, on, disabled, onChange }: { icon: 'bellSlash'; title: string; subtitle?: string; on: boolean; disabled?: boolean; onChange(on: boolean): void }) {
  return (
    <button className="list-row clickable cp-switch-row" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)}>
      <span className="list-row-leading">
        <Icon name={icon} size={18} />
      </span>
      <span className="list-row-text">
        <span className="list-row-title">{title}</span>
        {subtitle ? <span className="list-row-subtitle">{subtitle}</span> : null}
      </span>
      <span className={`cp-switch${on ? ' on' : ''}`} aria-hidden />
    </button>
  );
}
